/**
 * AuditoriaCierres.jsx · v101.264.0 · OT SMART › Auditoría de cierres.
 * Revisa las OTs en ejecución / fiscalización / con error de cierre (y las cerradas recientes) y muestra, antes de que
 * Odoo rechace, qué falta, quién debe resolverlo y cómo. Solo lectura.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Search, ShieldAlert, ShieldCheck, AlertTriangle, Info, ChevronDown, ExternalLink } from "lucide-react";

const C = { bg: "#E4EAF0", surf: "#ECF0F5", ink: "#0A3747", text: "#1E293B", sub: "#475569", mute: "#64748B",
  lime: "#E0E621", info: "#0284C7", warn: "#D97706", bad: "#DC2626", ok: "#16A34A", teal: "#5D8B96" };
const OUT_LG = "-10px -10px 22px rgba(255,255,255,.95), 10px 10px 22px rgba(163,177,198,.55)";
const OUT_SM = "-3px -3px 8px rgba(255,255,255,.95), 3px 3px 8px rgba(163,177,198,.55)";
const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const card = { padding: 22, borderRadius: 24, background: C.surf, boxShadow: OUT_LG, display: "flex", flexDirection: "column", gap: 14, minWidth: 0 };
const well = { borderRadius: 14, background: C.bg, boxShadow: IN_SM };
const SEV = {
  bloquea: { color: C.bad, label: "Bloquea el cierre", Icon: ShieldAlert },
  advertencia: { color: C.warn, label: "Advertencia", Icon: AlertTriangle },
  info: { color: C.info, label: "Información", Icon: Info },
};
const NOMBRE = {
  red_incompleta: "Falta puerto / caja", ip_otra_vlan: "IP de otra VLAN", ip_ocupada: "IP ocupada",
  equipo_dado_de_baja: "Equipo nuevo dado de baja", serie_no_existe: "Serie inexistente en Odoo",
  serie_otro_producto: "Serie de otro producto", serie_fuera_de_bodega: "Serie fuera de bodega",
  serie_ya_en_cliente: "Serie ya en un cliente", material_repetido: "Equipo repetido",
  devolucion_no_corresponde: "Devolución no corresponde", devolucion_forma_deprecada: "Devolución a registro de baja",
  corporativo_con_wifi: "Corporativo con WiFi", cerrada_sin_ejecutar: "Cerrada sin ejecutar",
};
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

function Kpi({ label, valor, color, Icon }) {
  return (
    <div style={{ ...card, padding: 18, gap: 6 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: C.sub }}>{Icon && <Icon size={14} color={color} />}{label}</span>
      <span style={{ fontSize: 36, fontWeight: 800, color: color || C.ink, letterSpacing: "-.02em" }}>{valor ?? "—"}</span>
    </div>
  );
}

function Chip({ activo, onClick, children, color }) {
  return (
    <button onClick={onClick} style={{ border: 0, cursor: "pointer", fontSize: 12, fontWeight: activo ? 800 : 600, padding: "7px 12px", borderRadius: 12,
      color: activo ? "#fff" : (color || C.sub), background: activo ? (color || C.ink) : C.surf, boxShadow: activo ? "none" : OUT_SM }}>{children}</button>
  );
}

function Tarjeta({ o }) {
  const [abierta, setAbierta] = useState(o.bloqueos > 0);
  const tono = o.bloqueos ? C.bad : o.advertencias ? C.warn : C.ok;
  return (
    <div style={{ ...card, padding: 0, gap: 0, overflow: "hidden" }} data-testid={`aud-ot-${o.ot}`}>
      <button onClick={() => setAbierta((v) => !v)} style={{ border: 0, background: "transparent", cursor: "pointer", textAlign: "left",
        display: "grid", gridTemplateColumns: "6px 1fr auto", alignItems: "stretch" }}>
        <i style={{ background: tono }} />
        <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <b style={{ fontFamily: MONO, fontSize: 16, color: C.ink }}>{o.ot}</b>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{o.actividad}</span>
          </span>
          <span style={{ fontSize: 12, color: C.sub }}>{o.sucursal || "Sin sucursal"} · {o.estado}{o.tecnico ? ` · ${o.tecnico}` : ""}{o.cierre_odoo ? ` · cierre: ${o.cierre_odoo}` : ""}</span>
          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            {o.hallazgos.filter((h) => h.severidad !== "info").map((h, i) => (
              <span key={i} style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 9, color: SEV[h.severidad].color, background: C.bg, boxShadow: IN_SM }}>
                {NOMBRE[h.codigo] || h.titulo}</span>))}
            {!o.bloqueos && !o.advertencias && <span style={{ fontSize: 11, fontWeight: 700, color: C.ok }}>Sin hallazgos</span>}
          </span>
        </div>
        <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 10 }}>
          {o.bloqueos > 0 && <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 800, color: C.bad }}>{o.bloqueos} bloq.</span>}
          {o.advertencias > 0 && <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 800, color: C.warn }}>{o.advertencias} adv.</span>}
          <ChevronDown size={18} color={C.mute} style={{ transform: abierta ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
        </div>
      </button>
      {abierta && (
        <div style={{ padding: "0 18px 18px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
          {o.hallazgos.map((h, i) => {
            const S = SEV[h.severidad] || SEV.info;
            return (
              <div key={i} style={{ ...well, padding: 14, display: "grid", gridTemplateColumns: "28px 1fr", gap: 10 }}>
                <S.Icon size={20} color={S.color} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <b style={{ fontSize: 13, color: C.ink }}>{h.titulo}</b>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.teal }}>{h.grupo} · {h.responsable}</span>
                  </span>
                  <span style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{h.detalle}</span>
                  {h.accion && <span style={{ fontSize: 12, color: C.sub }}><b style={{ color: S.color }}>Qué hacer:</b> {h.accion}</span>}
                </div>
              </div>
            );
          })}
          <a href={`/ot-smart/${o.odoo_task_id}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: C.info }}>
            Abrir la OT <ExternalLink size={13} /></a>
        </div>
      )}
    </div>
  );
}

export default function AuditoriaCierres() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suc, setSuc] = useState("");
  const [dias, setDias] = useState(15);
  const [q, setQ] = useState("");
  const [sev, setSev] = useState("");
  const [resp, setResp] = useState("");
  const [cod, setCod] = useState("");

  const cargar = useCallback(async (refrescar = false) => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/v1/auditoria-cierres", { params: { sucursal: suc || undefined, dias, refrescar }, timeout: 180000 });
      setData(d);
      if (!d.odoo_ok) toast.warning("Odoo no respondió: se muestran solo las revisiones de AlfaField.");
    } catch (e) { toast.error(errMsg(e, "No se pudo auditar")); } finally { setBusy(false); }
  }, [suc, dias]);
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const [sucs, setSucs] = useState([]);
  useEffect(() => {   // la lista de sucursales se acumula para no perder opciones al filtrar
    const n = (data?.ots || []).map((o) => o.sucursal).filter(Boolean);
    setSucs((prev) => [...new Set([...prev, ...n])].sort());
  }, [data]);
  const ots = useMemo(() => (data?.ots || []).filter((o) => {
    if (q && !`${o.ot} ${o.odoo_task_id} ${o.tecnico}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (sev === "bloquea" && !o.bloqueos) return false;
    if (sev === "advertencia" && (o.bloqueos || !o.advertencias)) return false;
    if (sev === "limpia" && (o.bloqueos || o.advertencias)) return false;
    if (resp && !o.hallazgos.some((h) => h.responsable === resp)) return false;
    if (cod && !o.hallazgos.some((h) => h.codigo === cod)) return false;
    return true;
  }), [data, q, sev, resp, cod]);

  const exportar = async () => {
    try {
      const r = await api.get("/v1/auditoria-cierres/export.xlsx", { params: { sucursal: suc || undefined, dias }, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `auditoria_cierres_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "No se pudo exportar")); }
  };

  const r = data?.resumen || {};
  return (
    <Layout>
      <div style={{ background: C.bg, borderRadius: 28, padding: "8px 4px", display: "flex", flexDirection: "column", gap: 20 }} data-testid="auditoria-cierres">
        <header style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.mute, letterSpacing: ".12em" }}>OT SMART · AUDITORÍA DE CIERRES</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: C.ink, margin: "4px 0" }}>Auditoría previa al cierre</h1>
            <p style={{ fontSize: 13, color: C.sub, margin: 0, maxWidth: 760 }}>
              Revisa contra Odoo (solo lectura) las OTs en ejecución, en fiscalización o con error de cierre, y las cerradas de los
              últimos días: qué va a rechazar Odoo, quién debe resolverlo y cómo.</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={exportar} style={{ border: 0, cursor: "pointer", display: "flex", gap: 8, alignItems: "center", padding: "10px 16px", borderRadius: 13,
              fontWeight: 700, fontSize: 13, color: C.ink, background: C.surf, boxShadow: OUT_SM }}><Download size={15} /> Excel</button>
            <button onClick={() => cargar(true)} disabled={busy} style={{ border: 0, cursor: "pointer", display: "flex", gap: 8, alignItems: "center", padding: "10px 16px",
              borderRadius: 13, fontWeight: 700, fontSize: 13, color: C.ink, background: C.lime, boxShadow: OUT_SM }}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Volver a auditar</button>
          </div>
        </header>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
          <Kpi label="OTs auditadas" valor={r.auditadas} />
          <Kpi label="Con bloqueo" valor={r.con_bloqueo} color={C.bad} Icon={ShieldAlert} />
          <Kpi label="Solo advertencias" valor={r.con_advertencia} color={C.warn} Icon={AlertTriangle} />
          <Kpi label="Listas para cerrar" valor={r.limpias} color={C.ok} Icon={ShieldCheck} />
        </div>

        <section style={{ ...card, gap: 12 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Buscar OT / técnico
              <span style={{ position: "relative" }}><Search size={14} color={C.mute} style={{ position: "absolute", left: 10, top: 11 }} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="00337821" style={{ border: 0, padding: "9px 12px 9px 30px", borderRadius: 12, fontSize: 13, background: C.bg, boxShadow: IN_SM, width: 200 }} /></span>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Sucursal
              <select value={suc} onChange={(e) => setSuc(e.target.value)} style={{ border: 0, padding: "9px 12px", borderRadius: 12, fontSize: 13, background: C.bg, boxShadow: IN_SM, minWidth: 170 }}>
                <option value="">Todas</option>{sucs.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Cerradas de los últimos
              <select value={dias} onChange={(e) => setDias(Number(e.target.value))} style={{ border: 0, padding: "9px 12px", borderRadius: 12, fontSize: 13, background: C.bg, boxShadow: IN_SM }}>
                {[7, 15, 30, 60].map((d) => <option key={d} value={d}>{d} días</option>)}</select>
            </label>
            <button onClick={() => cargar(true)} style={{ border: 0, cursor: "pointer", padding: "9px 14px", borderRadius: 12, fontSize: 13, fontWeight: 700, color: C.ink, background: C.surf, boxShadow: OUT_SM }}>Aplicar</button>
            {data && <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 11, color: C.mute }}>
              {data.generado?.slice(11, 16)} · {data.segundos}s · Odoo {data.odoo_ok ? "OK" : "sin respuesta"}</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["", "Todas"], ["bloquea", "Con bloqueo"], ["advertencia", "Solo advertencias"], ["limpia", "Listas"]].map(([k, l]) => (
              <Chip key={k || "t"} activo={sev === k} onClick={() => setSev(k)} color={k === "bloquea" ? C.bad : k === "advertencia" ? C.warn : k === "limpia" ? C.ok : C.ink}>{l}</Chip>))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.mute }}>RESPONSABLE</span>
            {(r.por_responsable || []).map(([k, v]) => <Chip key={k} activo={resp === k} onClick={() => setResp(resp === k ? "" : k)}>{k} · {v}</Chip>)}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.mute }}>HALLAZGO</span>
            {(r.por_codigo || []).map(([k, v]) => <Chip key={k} activo={cod === k} onClick={() => setCod(cod === k ? "" : k)}>{NOMBRE[k] || k} · {v}</Chip>)}
          </div>
        </section>

        {busy && !data && <div style={{ ...card, color: C.sub, flexDirection: "row", gap: 10 }}><Loader2 className="animate-spin" /> Auditando OTs contra Odoo…</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {ots.map((o) => <Tarjeta key={o.odoo_task_id} o={o} />)}
          {data && !ots.length && <div style={{ ...card, color: C.sub }}>Ninguna OT con esos filtros.</div>}
        </div>
      </div>
    </Layout>
  );
}
